/**
 * Environment configuration utilities
 */

export type Environment = "development" | "maintenance" | "testing" | "production";

export function getCurrentEnvironment(): Environment {
  // Runtime env vars may be undefined even if Vite types say otherwise
  const rawEnv = import.meta.env.VITE_ENVIRONMENT as string | undefined;
  const env = (rawEnv ?? "development").toLowerCase() as Environment;
  const validEnvironments: Environment[] = ["development", "maintenance", "testing", "production"];

  if (validEnvironments.includes(env)) {
    return env;
  }

  // Default to development if not specified or invalid
  return "development";
}

export function isEnvironment(env: Environment): boolean {
  return getCurrentEnvironment() === env;
}

export function isMaintenanceMode(): boolean {
  return isEnvironment("maintenance");
}

export function isProduction(): boolean {
  return isEnvironment("production");
}

export function isDevelopment(): boolean {
  return isEnvironment("development");
}

export function isTesting(): boolean {
  return isEnvironment("testing");
}

/**
 * Check if the current environment allows a specific feature
 */
export function isFeatureAllowed(allowedEnvironments: Environment[]): boolean {
  return allowedEnvironments.includes(getCurrentEnvironment());
}

/**
 * Environment-specific feature flags
 */
export const FEATURE_FLAGS = {
  // Auth test page: available in development, maintenance, testing
  AUTH_TEST: ["development", "maintenance", "testing"] as Environment[],

  // Debug logging: available in development and testing
  DEBUG_LOGGING: ["development", "testing"] as Environment[],

  // Analytics: only in production
  ANALYTICS: ["production"] as Environment[],

  // Beta features: development and testing
  BETA_FEATURES: ["development", "testing"] as Environment[],
} as const;
